// multiPageApp.js — pure assembly for AI-generated MULTI-PAGE apps (the /studio
// "create a full app from a prompt" path). Dependency-free (no smbls / no DOM) so
// it's unit-testable under `node --test`.
//
// WHY THIS EXISTS
// An "extension" is a Project with ONE source page rendered instantly via
// createIframeApp (no frank/publish). A full app is the SAME model with N source
// pages + a nav app-shell. renderMultiPageApp (functions/index.js) parses each
// page, seeds the shared root state, then hands { pages, app-shell } to
// createIframeApp. This module owns the PURE parts: normalizing the page list
// (routes/titles) and building the deterministic nav app-shell — the two pieces
// worth pinning with tests.
//
// THE APP-SHELL — state-based page switcher (NOT the framework URL router).
// The framework's `content`-outlet router does NOT fire through the
// createIframeApp embed path (verified live: the reserved `content` key renders
// its props as literal children and the RenderRouter lifecycle never triggers in
// the reduced iframe boot). Rather than reach into router internals across the
// realm boundary, the shell switches pages via reactive state — the robust,
// verified DOMQL pattern:
//   - the shell's own state `_appRoute` holds the active route (default '/')
//   - every page mounts as a `hide`-gated child (`hide` is the ONE reactive
//     visibility toggle in v3.14; only the active page shows)
//   - each nav button sets `_appRoute` on click
// This is fully client-side, deterministic, and free of the router-through-embed
// fragility. Real URL routing (deep-linkable) is a deferred enhancement that
// would need router init wired into the shared createIframeApp boot.

// Normalize a route string to a URL-safe path: lowercase, '/'-prefixed, spaces
// and punctuation → single hyphens, collapsed slashes, no trailing slash.
export const cleanRoute = (route) => {
  let r = String(route || '')
    .trim()
    .toLowerCase()
  if (!r || r === '/') return '/'
  if (r[0] !== '/') r = '/' + r
  // Keep slashes (nested routes) and alphanumerics; everything else → '-'.
  r =
    '/' +
    r
      .slice(1)
      .replace(/[^a-z0-9/]+/g, '-')
      .replace(/-*\/-*/g, '/')
      .replace(/^-+|-+$/g, '')
  r = r.replace(/\/+/g, '/').replace(/\/$/, '')
  return r || '/'
}

// Derive a human title from a route SLUG when the model didn't supply one. The
// root route has no slug — its word comes from the caller (`labels.home` below).
const _mpaTitleFromRoute = (route) => {
  const seg = String(route || '/')
    .split('/')
    .filter(Boolean)
    .pop()
  if (!seg) return ''
  return seg
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim()
}

// Normalize the model's `pages` input to a clean, deduped list:
//   - drops entries with no non-empty `source`
//   - forces the FIRST surviving page to route '/' (the app entry)
//   - slugifies + de-duplicates every other route
//   - fills a sensible title where missing
// `labels.home` is the word for the root page when the model gave it no title.
// It comes from the CALLER (which has the shell's polyglot) — this module is pure
// and holds no copy: a nav button that read the English "Home" on a Georgian board
// was the shell's own fallback, rendered in the shell.
// Returns [{ route, title, source }] (possibly empty).
export const normalizePages = (pages, labels = {}) => {
  const list = Array.isArray(pages) ? pages : []
  const out = []
  const seen = new Set()
  for (const p of list) {
    const source = p && typeof p.source === 'string' ? p.source.trim() : ''
    if (!source) continue
    const isFirst = out.length === 0
    let route = isFirst ? '/' : cleanRoute(p && p.route)
    if (seen.has(route)) {
      const base = route === '/' ? '/page' : route
      let n = 2
      while (seen.has(`${base}-${n}`)) n += 1
      route = `${base}-${n}`
    }
    seen.add(route)
    const rawTitle = p && typeof p.title === 'string' ? p.title.trim() : ''
    const title = rawTitle || (route === '/' ? labels.home || '' : _mpaTitleFromRoute(route))
    out.push({ route, title, source })
  }
  // Guarantee a '/' entry exists even if the first survivor was reassigned.
  if (out.length && !out.some((p) => p.route === '/')) out[0].route = '/'
  return out
}

// Nav model for the app-shell: [{ route, title }] in declared order.
export const buildNav = (normalizedPages) =>
  (normalizedPages || []).map((p) => ({ route: p.route, title: p.title }))

// Build the deterministic nav app-shell (the createIframeApp `shell`/root). A
// `flow:'column'` root with a persistent top `Nav` (a Brand + one button per
// route) over a `Body` that stacks every page as a `hide`-gated child — only the
// page whose route matches the shell's `_appRoute` shows. The shell may itself
// be nested inside the workspace: `s.root` belongs to that workspace, never to
// this preview. `pageDefs` is the parsed route→def map; `seedState`, when present,
// is merged into the shell state (see extensionDataSeed.js).
// `appLabel` is the brand word for an app with no name — from the caller, as
// `labels.home` above.
export const buildAppShell = ({ name, nav, pageDefs, seedState, appLabel } = {}) => {
  // Nav buttons — key `NavBtn_N` (PascalCase, uniqueness via `_N`). Each sets the
  // active route; active button wears the house active-state (title text + weight).
  const navChildren = {}
  ;(nav || []).forEach((n, i) => {
    navChildren[`NavBtn_${i + 1}`] = {
      tag: 'button',
      type: 'button',
      text: n.title,
      border: '0',
      background: 'none',
      cursor: 'pointer',
      padding: 'Y Z1',
      round: 'X2',
      fontWeight: (el, s) => (s._appRoute === n.route ? '600' : '500'),
      color: (el, s) => (s._appRoute === n.route ? 'title' : 'caption'),
      onClick: (e, el, s) => s.update({ _appRoute: n.route })
    }
  })

  // Page slots — key `Page_N`. Each wraps the parsed page def and shows only when
  // its route is active. The wrapper carries NO inline `display` (no flow/align),
  // so `hide` (→ display:none) applies cleanly instead of losing to an inline
  // display the way it does over a flex child (domql-hide-loses-to-inline-display).
  const pageChildren = {}
  Object.entries(pageDefs || {}).forEach(([route, def], i) => {
    pageChildren[`Page_${i + 1}`] = {
      width: '100%',
      display: null,
      hide: (el, s) => s._appRoute !== route,
      Content: def
    }
  })

  return {
    flow: 'column',
    minHeight: '100%',
    // House page surface — the same design-system theme every native workspace
    // page renders on, so a multi-page app reads like a native module page.
    // (Was a `_paintHostSurface` copy of the host's computed colors while the
    // embed iframe resolved no theme vars; both causes of that are fixed — see
    // _applyPlatformSurface in index.js.)
    theme: 'document',
    overflow: 'hidden',
    // Navigation stays local even when two generated previews share a workspace.
    state: {
      _appRoute: '/',
      ...(seedState && typeof seedState === 'object' ? seedState : {})
    },
    Nav: {
      tag: 'nav',
      flow: 'row',
      align: 'center space-between',
      gap: 'C',
      padding: 'B C',
      // App-bar hairline so the nav reads as chrome above the page body.
      flexShrink: '0',
      borderBottomWidth: 'hairline',
      borderBottomStyle: 'solid',
      borderColor: 'line',
      Brand: {
        tag: 'button',
        type: 'button',
        text: name || appLabel || '',
        fontWeight: '700',
        border: '0',
        background: 'none',
        cursor: 'pointer',
        onClick: (e, el, s) => s.update({ _appRoute: '/' })
      },
      Links: {
        flow: 'row',
        align: 'center',
        gap: 'Y',
        ...navChildren
      }
    },
    Body: {
      flex: '1',
      minHeight: '0',
      flow: 'y',
      // Page padding + scroll so each page reads like a workspace module page
      // (the page slots themselves carry no edge spacing).
      padding: 'B C',
      overflowY: 'auto',
      ...pageChildren
    }
  }
}
